import React, { useEffect, useState } from 'react';
import { translate } from '../../Services/translation';
import constants from '../../constants';
import { useBCApi } from './hooks/BCApi';
import { string, object } from 'yup';
import { setErrors } from './helpers/errors';
import { BCFormInput } from './BCFormInput';
import * as yup from 'yup';
import states from '../Checkout/States.json';

const addressValidationFields = {
  companyName: '',
  phoneNumber: '',
  country: '',
  city: '',
  state: '',
  zipCode: '',
  address2: '',
  address: '',
};

export const BCAddressItem = ({
  address,
  onDelete,
  fetch,
  showButtons,
  createForm,
  createRequest,
}) => {
  const [editing, setEditing] = useState(createForm);
  const [addressForm, setAddressForm] = useState(address);
  const [validationErrors, setValidationErrors] = useState({
    companyName: '',
    phoneNumber: '',
    country: '',
    city: '',
    state: '',
    zipCode: '',
    address2: '',
    address: '',
  });

  const { status, value, execute, error } = useBCApi(
    '/api/bcmypages/addresses',
    false
  );

  const addressSchema = object().shape({
    companyName: string().required(translate(`validation.required`)),
    phoneNumber: string().required(translate(`validation.required`)),
    country: string().required(translate(`validation.required`)),
    city: string().required(translate(`validation.required`)),
    zipCode: string().required(translate(`validation.required`)),
    address2: string().nullable(),
    address: string().required(translate(`validation.required`)),
    state: yup
      .string()
      .max(20, translate('checkout.forms.errors.maxlength', 20))
      .when('country', {
        is: (country) => ['US', 'CA'].includes(country),
        then: (schema) => schema.required(translate('forms.required')),
        otherwise: (schema) => schema.optional(),
      }),
  });

  const onChange = (e) => {
    setAddressForm({
      ...addressForm,
      [e.target.name]: e.target.value,
      ...(e.target.name === 'country' &&
        e.target.value === 'US' && { state: states['usa'][0].abbreviation }),
      ...(e.target.name === 'country' &&
        e.target.value === 'CA' && { state: states['canada'][0].abbreviation }),
      ...(e.target.name === 'country' &&
        e.target.value !== 'CA' &&
        e.target.value !== 'US' && { state: '' }),
    });
  };

  const submitForm = async () => {
    var result = await addressSchema
      .validate(addressForm, { abortEarly: false })
      .catch((err) => {
        const errors = err.inner.reduce((acc, error) => {
          return {
            ...acc,
            [error.path]: error.message,
          };
        }, {});

        setValidationErrors(() => {
          const newErrors = {};
          Object.keys(errors).forEach((key) => {
            newErrors[key] = errors[key] || '';
          });
          return newErrors;
        });

        return false;
      });

    setValidationErrors(addressValidationFields);

    if (createForm) {
      createRequest.execute('POST', addressForm);
    } else {
      execute('PUT', addressForm);
    }
  };

  useEffect(() => {
    if (status == 'success') {
      fetch();
      setValidationErrors(addressValidationFields);
    }
  }, [fetch, status]);

  useEffect(() => {
    if (error && status == 'error') {
      var errors = setErrors(error, validationErrors);
      setValidationErrors({ ...errors });
    }
  }, [error, status]);

  useEffect(() => {
    if (createRequest?.error && createRequest?.status == 'error') {
      var errors = setErrors(createRequest.error, validationErrors);
      setValidationErrors(() => ({
        ...errors,
      }));
    }
  }, [createRequest?.error, createRequest?.status]);

  return (
    <div className="BCAddressItem__row__container">
      <div className="BCAddressItem__row__container__head">
        <i className="BCAddressItem__row__container__GEOIcon" />
        <p className="BCAddressItem__row__container__address">
          {!editing
            ? `${address.address} ${address.zipCode} ${address.city}`
            : createForm
            ? translate('business.addressestab.creating')
            : translate('business.addressestab.editing')}
        </p>
        {showButtons && (
          <div className="BCAddressItem__row__container__iconGroup">
            {!createForm && (
              <i
                className="BCAddressItem__row__container__editIcon"
                onClick={() => setEditing(!editing)}
              />
            )}
            <i
              className="BCAddressItem__row__container__deleteIcon"
              onClick={onDelete}
            />
          </div>
        )}
      </div>
      {editing && (
        <div className="BCAddressItem__row__container__editform">
          <BCFormInput
            label={translate('business.addressestab.form.companyName')}
            name="companyName"
            value={addressForm.companyName}
            disabled={false}
            onChange={onChange}
            error={validationErrors.companyName}
          />
          <BCFormInput
            label={translate('business.addressestab.form.address')}
            name="address"
            value={addressForm.address}
            disabled={false}
            onChange={onChange}
            error={validationErrors.address}
          />
          <BCFormInput
            label={translate('business.addressestab.form.address2')}
            value={addressForm.address2}
            name="address2"
            disabled={false}
            onChange={onChange}
            error={validationErrors.address2}
          />
          <BCFormInput
            label={translate('business.addressestab.form.zipcode')}
            name="zipCode"
            value={addressForm.zipCode}
            disabled={false}
            onChange={onChange}
            error={validationErrors.zipCode}
          />
          <BCFormInput
            label={translate('business.addressestab.form.city')}
            name="city"
            value={addressForm.city}
            disabled={false}
            onChange={onChange}
            error={validationErrors.city}
          />
          <div className="BCFormInput__container">
            <label className="BCFormInput__label">
              {translate('business.addressestab.form.country')}
            </label>
            <select
              className="form__input form__input--marginless"
              autoComplete="country-name"
              value={addressForm.country || ''}
              onChange={onChange}
              name="country"
            >
              {constants.countries
                .filter((x) => {
                  if (constants.deliveryAddressCountries.length > 0) {
                    return constants.deliveryAddressCountries.includes(x.value);
                  }

                  return true;
                })
                .map((country) => (
                  <option key={country.value} value={country.value}>
                    {country.text}
                  </option>
                ))}
            </select>
          </div>
          {addressForm.country === 'US' || addressForm.country === 'CA' ? (
            <div className="BCFormInput__container">
              <label className="BCFormInput__label">
                {translate('business.addressestab.form.state')}
              </label>
              <select
                className="form__input form__input form__input--marginless"
                autoComplete="state"
                value={addressForm.state || ''}
                onChange={onChange}
                name="state"
              >
                {states[addressForm.country === 'US' ? 'usa' : 'canada'].map(
                  (country) => (
                    <option
                      key={country.abbreviation}
                      value={country.abbreviation}
                    >
                      {country.name}
                    </option>
                  )
                )}
              </select>
            </div>
          ) : (
            <BCFormInput
              label={translate('business.addressestab.form.state')}
              name="state"
              value={addressForm.state}
              disabled={false}
              onChange={onChange}
              error={validationErrors.state}
            />
          )}

          <BCFormInput
            label={translate('business.addressestab.form.careof')}
            name="careOf"
            value={addressForm.careOf}
            disabled={false}
            onChange={onChange}
          />
          <BCFormInput
            label={translate('business.addressestab.form.phone')}
            name="phoneNumber"
            value={addressForm.phoneNumber}
            disabled={false}
            onChange={onChange}
            error={validationErrors.phoneNumber}
          />

          <button
            className="BCAddressItem__button"
            onClick={() => submitForm()}
          >
            <p>{translate('general.save')}</p>
          </button>
        </div>
      )}
    </div>
  );
};
